import {
   Component,
   OnInit,
   Pipe,
   PipeTransform,
   Injectable,
   inject,
   signal,
   computed,
   effect,
   ViewEncapsulation,
   ElementRef,
   ViewChild,
   AfterViewInit,
   HostListener,
   Input,
} from '@angular/core'
import { DatePipe } from '@angular/common'
import { Subscription, interval, map, timer } from 'rxjs'
import { LeaderboardClient, LeaderboardData, LeaderboardGoal, LeaderboardWeeklyData, LeaderboardDailyData } from 'src/app/shared/services/ppv.api'
import { addDays, isEqual } from 'date-fns'
import { ApexNonAxisChartSeries, ApexChart, ApexPlotOptions } from 'ng-apexcharts'
import { ApexAxisChartSeries, ApexXAxis, ApexYAxis, ApexFill, ApexStroke, ApexLegend, ApexDataLabels, ApexTooltip, ApexOptions } from 'ng-apexcharts'
import { LeaderboardService } from './leaderboard.service'

@Component({
   selector: 'app-leaderboard',
   templateUrl: './leaderboard.component.html',
   styleUrls: ['./leaderboard.component.scss'],
   standalone: false,
   encapsulation: ViewEncapsulation.None,
})
@Injectable({
   providedIn: 'root',
})
export class LeaderboardComponent implements OnInit {
   private leaderboardService = inject(LeaderboardService)

   showChart = false

   selectedKey = 0
   zohoFeedback = ''

   practices = this.leaderboardService.practices
   selectedPracticeData = this.leaderboardService.currentPracticeData
   selectedPracticeDataScans = this.leaderboardService.currentPracticeDataScans

   //TODO: update this
   contestOptions = [
      { label: 'Contest 1 (Jul 14 - Aug 10)', value: 1 },
      { label: 'Contest 2 (Aug 11 - Sep 7)', value: 2 },
      { label: 'Contest 3 (Sep 8 - Oct 6)', value: 3 },
   ]
   selectedContestNumber: number | null = 3

   impromedPractices = [
      29, 37, 48, 55, 77, 82, 91, 112, 147, 151, 1287, 1295, 1296, 1335, 1337, 1380, 1392, 1398, 1417, 1421, 1438, 1513, 1534, 1536, 1550, 1555, 1580,
      1581, 1588, 1638, 1639, 1640, 1654, 1656, 1658, 1670, 1671, 1934, 1939, 1948, 1953, 1960, 1964, 1986, 2002, 2030, 2042, 2050, 2082, 2084, 2102,
      2103,
   ]
   omittedPracticesForContestReqs = [...this.impromedPractices]
   isOmitted = computed(() => this.omittedPracticesForContestReqs.includes(this.selectedPracticeSKey()))

   selectedPracticeSKey = signal<number>(0)
   selectedPractice = computed(() => this.practices().find((p) => p.practiceSKey === this.selectedPracticeSKey()))

   allScansStats = this.leaderboardService.scansStats
   regionalScanStats = computed(() => this.allScansStats().filter((prac) => prac.rmUserId == this.selectedPractice()?.rmUserId))
   scansStats = computed(() => this.allScansStats()?.find((s) => s.practiceSKey === this.selectedPracticeSKey())?.stats)
   scansGoal = computed(() => this.allScansStats()?.find((s) => s.practiceSKey === this.selectedPracticeSKey())?.goal)

   totalBloodTestsAdjusted = signal(this.scansStats()?.totalBloodTests ?? 0)

   showBloodTests = computed(() => this.selectedPractice()?.isCanada)
   showLinks = computed(() => !this.selectedPractice()?.isCanada)

   loading = computed(() => !this.practices() || !this.selectedPracticeData() || !this.selectedPractice())
   activeGoal = computed(() => !this.loading() && this.selectedPractice()?.goal?.totalPets > 0)
   activeBloodGoal = computed(() => !this.loading() && this.selectedPractice()?.goal?.totalBloodTests > 0)

   regionalPractices = computed(() => this.practices().filter((prac) => prac.rmUserId == this.selectedPractice()?.rmUserId))

   totalVetcorSV = computed(() => this.practices()?.reduce((acc, p) => acc + p.stats?.totalPets, 0))
   totalVetcoveLinks = computed(() => this.practices()?.reduce((acc, p) => acc + p.stats?.totalLinksSent, 0))
   totalBloodTests = computed(() => this.practices()?.reduce((acc, p) => acc + p.stats?.totalBloodTests, 0))

   fillPercent = computed(() => this.selectedPractice()?.stats?.petsProgressPct || 0)
   fillPercentScans = computed(() => this.scansStats()?.bloodTestsProgressPct || 0)

   remainingPets = computed(() => this.selectedPractice()?.goal?.totalPets - this.selectedPractice()?.stats?.totalPets)
   remainingScans = computed(() => this.scansGoal()?.totalBloodTests - this.totalBloodTestsAdjusted())
   remainingWeeks = computed(() => this.getRemainingWeeks(this.selectedPractice()?.goal))
   remainingPetsPerWeek = computed(() => {
      const remaining = this.remainingPets()
      const weeks = this.remainingWeeks()

      //console.log('pets: ', { remaining, weeks })
      if (typeof remaining !== 'number' || typeof weeks !== 'number' || weeks <= 0) return null
      return Math.ceil(remaining / weeks)
   })

   remainingScansPerWeek = computed(() => {
      const remaining = this.remainingScans()
      let weeks = this.getRemainingWeeksForScans(this.scansGoal())

      //console.log('scans: ', { remaining, weeks })
      if (typeof remaining !== 'number' || typeof weeks !== 'number' || weeks <= 0) return null
      return Math.ceil(remaining / weeks)
   })

   getRemainingWeeks(goal: LeaderboardGoal) {
      const startDate = new Date() > goal?.startDate ? new Date() : goal?.startDate
      const weeksLeft = Math.ceil((goal?.endDate?.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 7))
      return weeksLeft
   }

   getRemainingWeeksForScans(goal: LeaderboardGoal) {
      const startDate = new Date() > addDays(goal?.startDate, 7) ? new Date() : addDays(goal?.startDate, 7)
      const endDate = addDays(goal?.endDate, 7)
      const weeksLeft = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 7))
      return weeksLeft
   }

   createChartSeries = (): ApexAxisChartSeries => [
      { name: 'Goal', data: [0] },
      { name: 'Actual', data: [0] },
   ]

   chartSeries = this.createChartSeries()
   chartSeriesScans = this.createChartSeries()

   createChartOptions = (labelText: string): ApexOptions => ({
      chart: {
         type: 'area',
         height: 250,
         zoom: { enabled: false },
         toolbar: {
            tools: {
               zoom: false,
               zoomin: false,
               zoomout: false,
               pan: false,
               selection: false,
            },
         },
         width: '100%',
         stacked: false,
         animations: { enabled: true },
      },
      xaxis: {
         type: 'datetime',
         labels: {
            formatter: (value) => new Date(value).toLocaleDateString(),
         },
      },
      yaxis: {
         min: 0,
         max: 0,
         tickAmount: 4,
         labels: { formatter: () => '' },
      },
      markers: {
         size: 0,
         colors: ['#ff7f0e'],
         strokeWidth: 5,
         strokeColors: '#ff7f0e',
         hover: { size: 3 },
      },
      stroke: {
         width: [2, 4],
         curve: 'monotoneCubic',
      },
      legend: { position: 'bottom' },
      dataLabels: { enabled: false },
      tooltip: {
         shared: true,
         custom: ({ dataPointIndex, w }) => {
            const goalV = w.globals.series[0][dataPointIndex]
            const actualV = w.globals.series[1][dataPointIndex]

            let label = `<div class="petsHelpedChartTooltip"><span class="v-orangecolor">Goal:</span> &nbsp;${Math.round(goalV)} ${labelText}`
            if (!isNaN(actualV)) {
               label += `<br><span class="v-darkorangecolor">Actual:</span> &nbsp;${Math.round(actualV)} ${labelText}</div>`
            }
            return label
         },
         x: { show: false },
         intersect: false,
      },
   })

   chartOptions = signal<ApexOptions>(this.createChartOptions('Pets'))
   chartOptionsScans = signal<ApexOptions>(this.createChartOptions('Screens'))

   constructor(public leaderboardClient: LeaderboardClient) {
      effect(() => {
         this.leaderboardService.loadPractice(
            this.selectedPracticeSKey(),
            this.selectedContestNumber,
            this.selectedPractice()?.goal?.startDate,
            null,
            'pets'
         )
         if (this.selectedPractice()?.isCanada) {
            this.leaderboardService.loadPractice(
               this.selectedPracticeSKey(),
               this.selectedContestNumber,
               this.selectedPractice()?.goal?.startDate,
               this.selectedPractice()?.goal?.endDate,
               'scans'
            )
         }
      })

      effect(() => {
         this.updateChartOptions(this.selectedPractice()?.goal, this.selectedPracticeData()?.dailyData, 'pets')
         this.updateChartOptions(this.scansGoal(), this.selectedPracticeDataScans()?.dailyData, 'scans')
      })

      effect(() => {
         if (this.selectedPractice()?.isCanada) {
            let contestToLoad = this.selectedPractice()?.goal.contestNumber

            // 1 week delayed...for historical won't matter
            if (addDays(this.selectedPractice()?.goal?.startDate, 7) > new Date()) {
               contestToLoad--
            }

            this.leaderboardService.loadScansStats(contestToLoad)
         }
      })
   }

   ngOnInit(): void {
      this.leaderboardService.loadPractices(this.selectedContestNumber)
   }

   getFillColor(fillType: string): string {
      const stats = fillType == 'pets' ? this.selectedPractice()?.stats?.totalPets : this.totalBloodTestsAdjusted()
      const goal = fillType == 'pets' ? this.selectedPractice()?.goal?.totalPets : this.scansGoal()?.totalBloodTests

      if (stats >= goal) return '#00b386'
      else if (stats > 0) return '#03af9c'
      else return '#ffffff'
   }

   formatDollarValue = () => {
      const rawValue = this.selectedPractice()?.stats?.linksTotalBonus ?? 0
      const computed = Math.ceil(rawValue * 1)
      return '$' + computed.toLocaleString()
   }

   private updateChartOptions(goal: LeaderboardGoal, dailyData: LeaderboardDailyData[], chartKey: 'pets' | 'scans' = 'pets') {
      if (!goal || !dailyData) return

      const DAY = 24 * 60 * 60 * 1000
      const shiftDays = chartKey === 'scans' ? 7 : 0

      const start = new Date(goal.startDate.getTime() + shiftDays * DAY)
      const end = new Date(goal.endDate.getTime() + shiftDays * DAY)

      const lp = this.selectedPractice()?.lastProcessDate ? new Date(this.selectedPractice()!.lastProcessDate) : null
      const endCap = lp ? new Date(Math.min(end.getTime(), lp.getTime())) : end

      const goalTotal = chartKey === 'pets' ? goal?.totalPets ?? 0 : goal?.totalBloodTests ?? 0
      const chartOptionsTarget = chartKey === 'pets' ? this.chartOptions : this.chartOptionsScans
      const chartSeriesTarget = chartKey === 'pets' ? 'chartSeries' : 'chartSeriesScans'

      const valueTotal = chartKey === 'pets' ? dailyData.reduce((pv, d) => pv + (d.totalPetsHelped ?? 0), 0) : this.scansStats()?.totalBloodTests ?? 0

      const sortedDaily = dailyData
         .filter((r) => {
            const dt = new Date(r.date)
            return dt >= start && dt <= endCap
         })
         .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())

      const actualSeries: { x: string; y: number }[] = []
      let running = 0
      for (const sd of sortedDaily) {
         running += chartKey === 'pets' ? sd.totalPetsHelped ?? 0 : sd.totalBloodTests ?? 0
         actualSeries.push({ x: new Date(sd.date).toISOString(), y: running })
      }

      const totalDays = Math.max(1, Math.round((end.getTime() - start.getTime()) / DAY))
      const forecasted: { x: string; y: number }[] = []
      for (let i = 0; i <= totalDays; i++) {
         const d = new Date(start.getTime() + i * DAY)
         const y = Math.min(goalTotal, (goalTotal / totalDays) * i)
         forecasted.push({ x: new Date(d).toISOString(), y })
      }

      const newYaxis = {
         min: 0,
         max: Math.max(valueTotal, goalTotal),
         tickAmount: 4,
         forceNiceScale: false,
         title: {
            text: chartKey === 'pets' ? 'Pets Helped' : 'Screens Completed',
            offsetX: -5,
         },
         labels: {
            show: true,
            formatter: (val: number) => Math.round(val).toString(),
         },
      }

      const currentYaxis = Array.isArray(chartOptionsTarget().yaxis) ? chartOptionsTarget().yaxis[0] : chartOptionsTarget().yaxis
      const isEqual = currentYaxis?.max === newYaxis.max && currentYaxis?.tickAmount === newYaxis.tickAmount

      if (!isEqual) {
         chartOptionsTarget.set({
            ...chartOptionsTarget(),
            yaxis: newYaxis,
            xaxis: {
               ...chartOptionsTarget().xaxis,
               type: 'datetime',
               tickAmount: 4,
            },
         })
      }

      this[chartSeriesTarget] = [
         {
            ...this[chartSeriesTarget][0],
            data: forecasted,
         },
         {
            ...this[chartSeriesTarget][1],
            data: actualSeries,
         },
      ]

      // IF NEEDED: To remove the added 7 days for health screens' data and thermometer, comment these two lines below
      this.totalBloodTestsAdjusted.set(running)
      this.fillPercentScans = computed(() => running / goalTotal || 0)
   }

   onPracticeSKeySelected(practiceSKey: number) {
      this.selectedPracticeSKey.set(practiceSKey)
   }

   onContestChange(value: number | null) {
      console.log('Contest changed to: ' + value)

      this.selectedContestNumber = value

      this.leaderboardService.loadPractices(this.selectedContestNumber)
   }
}
